import React, { useState } from 'react';
import { getTheme, useThemeMode } from '../theme';
import { bearerHeaders } from '../api';
import { apiFetch } from '../appFetch';
import { Button, Field, Input } from '../components/ui';

// AS1d · Profil → Radera konto (App Store-riktlinje 5.1.1(v)). Reglerna står i apps/api/src/services/accountDeletion.ts.

const CONFIRM_WORD = 'RADERA';
/** Dit man skriver för att få Qid-kontot borttaget (se rapporten för AS1d). */
const SUPPORT_EMAIL = 'info@qvenari.com';

interface MarkOutcome { markId: string; name: string; outcome: 'transfer' | 'dormant' | 'contact_only'; successorName?: string | null }
interface Plan { blockers: string[]; marks: MarkOutcome[]; keptPurchases: number }

async function call<T>(path: string, init?: RequestInit): Promise<T> {
  const res = await apiFetch(path, { ...init, headers: { 'Content-Type': 'application/json', ...bearerHeaders(), ...init?.headers } });
  const data = await res.json().catch(() => ({}));
  if (!res.ok) throw Object.assign(new Error(data?.error || 'Något gick fel'), { blockers: data?.blockers as string[] | undefined });
  return data as T;
}

export function DeleteAccountSection({ cardStyle, onDeleted }: { cardStyle: React.CSSProperties; onDeleted?: () => void }) {
  const T = getTheme(useThemeMode().mode);
  const [open, setOpen] = useState(false);
  const [plan, setPlan] = useState<Plan | null>(null);
  const [word, setWord] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [done, setDone] = useState(false);

  const sub: React.CSSProperties = { color: T.textSub, fontSize: 13, lineHeight: 1.55, margin: 0 };
  const head: React.CSSProperties = { color: T.text, fontSize: 13, fontWeight: 700, margin: '14px 0 4px' };
  const list: React.CSSProperties = { ...sub, paddingLeft: 18, margin: 0 };

  async function start() {
    setOpen(true); setError('');
    try { setPlan(await call<Plan>('/api/profile/account/deletion')); }
    catch (e: any) { setError(e.message); }
  }

  async function remove() {
    setBusy(true); setError('');
    try {
      await call('/api/profile/account/delete', { method: 'POST', body: JSON.stringify({ confirm: word }) });
      setDone(true);
      setTimeout(() => onDeleted?.(), 2500);
    } catch (e: any) {
      if (e.blockers?.length) setPlan(p => (p ? { ...p, blockers: e.blockers } : p));
      setError(e.message);
      setBusy(false);
    }
  }

  const label = <div style={{ color: T.textSub, fontSize: 11, fontWeight: 600, letterSpacing: '0.08em', textTransform: 'uppercase', marginBottom: 8 }}>Radera konto</div>;

  if (done) {
    return (
      <div style={{ ...cardStyle, marginBottom: 16 }} role="status">
        {label}
        <p style={{ ...sub, color: T.text }}>Ditt Qvenari-konto är raderat. Du loggas ut nu.</p>
      </div>
    );
  }

  if (!open) {
    return (
      <div style={{ ...cardStyle, marginBottom: 16, display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 14, flexWrap: 'wrap' }}>
        <div style={{ minWidth: 0, flex: 1 }}>
          {label}
          <p style={sub}>Ta bort ditt konto och dina uppgifter i Qvenari.</p>
        </div>
        <Button variant="secondary" size="sm" onClick={start} style={{ color: T.danger }}>Radera mitt konto…</Button>
      </div>
    );
  }

  const blocked = !!plan?.blockers.length;
  return (
    <div style={{ ...cardStyle, marginBottom: 16, border: `1px solid ${T.danger}` }}>
      {label}
      <p style={{ ...sub, color: T.text, fontWeight: 600 }}>Raderingen går inte att ångra.</p>

      <div style={head}>Det här tas bort</div>
      <ul style={list}>
        <li>Din profil, profilbild, jägarexamen, licenser, hundar, vapen och utrustning</li>
        <li>Alla positioner och spår från jakter, hundklockor och GPS-halsband</li>
        <li>Kopplingar till PAJ, Garmin, Traccar, Abeeway, Telegram och WhatsApp, notiser på dina enheter och anslutna AI-agenter (MCP)</li>
        <li>Ditt medlemskap i jaktmarker och jaktlag, dina ansökningar och köplatser för kött</li>
        <li>Bilder och filmer du laddat upp i album, chattar och jakter</li>
      </ul>

      <div style={head}>Det här anonymiseras</div>
      <ul style={list}>
        <li>Dina chattmeddelanden står kvar så att samtalen går att följa, men visas som "Raderad användare" (bilder och filer tas bort)</li>
        <li>Fällt vilt, köttfördelning och svar på jaktinbjudningar finns kvar i markens statistik utan ditt namn</li>
      </ul>

      <div style={head}>Det här behålls</div>
      <ul style={list}>
        <li>Köp där pengar dragits och signerade avtal sparas i 7 år enligt bokföringslagen{plan?.keptPurchases ? ` (${plan.keptPurchases} köp hos dig)` : ''}, men kopplas bort från ditt konto. Köp där inga pengar dragits anonymiseras.</li>
      </ul>

      {!!plan?.marks.length && <>
        <div style={head}>Dina jaktmarker</div>
        <ul style={list}>
          {plan.marks.map(m => (
            <li key={m.markId}>
              <strong style={{ color: T.text }}>{m.name}</strong>{' '}
              {m.outcome === 'transfer' && <>tas över av {m.successorName}.</>}
              {m.outcome === 'dormant' && <>har ingen annan med ledande roll och blir vilande: den avpubliceras, annonserna blir utkast och åtelkamerornas adresser slutar fungera. Övriga medlemmar behåller sin historik tills Qvenari utser en ny ägare.</>}
              {m.outcome === 'contact_only' && <>: du tas bort som kontaktperson.</>}
            </li>
          ))}
        </ul>
        <p style={{ ...sub, marginTop: 6 }}>Ditt Stripe-konto, Swishnummer och person- eller organisationsnummer tas bort från marken. Vill du lämna över till någon särskild: ge den personen rollen jaktledare innan du raderar.</p>
      </>}

      <div style={head}>Ditt Qid-konto</div>
      <p style={sub}>
        Du loggar in med Qid, som också används av Qvestium och Qdomus. Här raderas Qvenari-kontot och du loggas ut; Qid-kontot finns kvar så att du kan fortsätta använda de andra apparna.
        Vill du ta bort Qid-kontot helt, mejla <a href={`mailto:${SUPPORT_EMAIL}?subject=Radera%20mitt%20Qid-konto`} style={{ color: T.amber }}>{SUPPORT_EMAIL}</a> från adressen du loggar in med.
      </p>

      {blocked && (
        <div role="alert" style={{ marginTop: 14, padding: '10px 12px', borderRadius: 8, background: T.dangerSoft, color: T.text, fontSize: 13, lineHeight: 1.5 }}>
          <strong>Kontot kan inte raderas ännu</strong>
          <ul style={{ ...list, color: T.text, marginTop: 4 }}>{plan!.blockers.map(b => <li key={b}>{b}</li>)}</ul>
        </div>
      )}

      <div style={{ marginTop: 16 }}>
        <Field label={`Skriv ${CONFIRM_WORD} för att bekräfta`} error={error && !blocked ? error : undefined}>
          <Input value={word} onChange={e => setWord(e.target.value)} autoComplete="off" autoCapitalize="characters" spellCheck={false}
            placeholder={CONFIRM_WORD} disabled={busy || blocked || !plan} aria-label={`Skriv ${CONFIRM_WORD} för att bekräfta`} />
        </Field>
      </div>
      <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end', marginTop: 12, flexWrap: 'wrap' }}>
        <Button variant="secondary" onClick={() => { setOpen(false); setWord(''); setError(''); }} disabled={busy}>Avbryt</Button>
        <Button variant="danger" onClick={remove} loading={busy}
          disabled={busy || blocked || !plan || word.trim().toUpperCase() !== CONFIRM_WORD}>Radera kontot</Button>
      </div>
    </div>
  );
}
